import { useAuth, useOrganization } from '@clerk/clerk-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { billingKeys, orgMetricsKeys } from '@/lib/queryKeys'
import { updateBillingRateCard } from '../api/api'
import type { RateCardChangeRequest } from '../types'

/** Previews (`confirm: false`) or writes a rate card change for the session's
 *  organization. Only a confirmed change refetches the billing page and the
 *  home page's org metrics, which show the FTWE the card sets, including one
 *  that failed, since it may have landed; a preview changes nothing. */
export function useUpdateBillingRateCardMutation() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: RateCardChangeRequest) => {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      return updateBillingRateCard(token, body)
    },
    onSettled: (_result, _error, body) => {
      if (!body.confirm || !organization) return
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: billingKeys.all(organization.id),
        }),
        queryClient.invalidateQueries({
          queryKey: orgMetricsKeys.all(organization.id),
        }),
      ])
    },
  })
}
